import { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { mutate } from 'swr';

import { usePacksContext } from '@/components/app/layout/PacksProvider';
import GuideRow from '@/components/app/program/GuideRow';
import { GuideRowProps } from '@/components/app/program/GuideRow/types';
import MasterclassGuideRow from '@/components/app/program/MasterclassGuideRow';
import Loader from '@/components/Loader';
import { useSimplifiedSignupModalContext } from '@/context/SimplifiedSignupModalContextProvider';
import { useGuidePlay } from '@/hooks/app/useGuidePlay';
import useLockedContentUpsell from '@/hooks/app/useLockedContentUpsell';
import useProgramStats from '@/hooks/app/useProgramStats';

import messages from './messages';
import { Divider, Footer, Wrapper } from './styles';
import { GuideListProps } from './types';

const GuideRowComponent = ({ guide, program, programProgress }: GuideRowProps): ReactElement => {
	if (program && program.meditation_type === 'masterclass') {
		return <MasterclassGuideRow guide={guide} />;
	}

	return <GuideRow guide={guide} program={program} programProgress={programProgress} />;
};

const GuideList = ({ program }: GuideListProps): ReactElement => {
	const { formatMessage } = useIntl();
	const isMasterclass = program?.meditation_type === 'masterclass';

	const orderedGuides = program?.guides
		.filter(unOrdGuide => (isMasterclass ? !unOrdGuide.is_trailer : true))
		.sort((a, b) => (a.position > b.position ? 1 : -1));

	const { data: programProgress, isValidating } = useProgramStats(program);
	// Only the first fetch shows the loader, so a revalidation after a finished session keeps the rows mounted.
	const isLoading = isValidating && programProgress === undefined;

	const onGuidePlay = useGuidePlay();
	const { setIsModalOpen } = useSimplifiedSignupModalContext();
	const { currentPackItem } = usePacksContext();
	const resumeGuideId =
		currentPackItem && 'guide_id' in currentPackItem ? currentPackItem.guide_id : undefined;

	// A locked tap opens the upsell with the tapped guide as the resume target, so a purchase
	// ends in that guide playing.
	useLockedContentUpsell({
		onHandoff: async () => {
			await mutate(`stats/programs/${program.id}`);
			if (resumeGuideId) {
				await onGuidePlay(resumeGuideId, program);
			}
			setIsModalOpen(false);
		},
	});

	// iOS withholds the total on timer programs, where a row's length is a bell interval rather than
	// a session length.
	const showFooter = !isMasterclass && program?.meditation_type !== 'timer' && Boolean(orderedGuides?.length);
	const totalMinutes = Math.floor(
		(orderedGuides ?? []).reduce((total, g) => total + (g.asset?.duration ?? 0), 0) / 60,
	);

	if (isLoading) {
		return <Loader />;
	}

	return (
		<Wrapper role="list" $hasTopDivider={!isMasterclass}>
			{isMasterclass && <Divider isExtended />}
			{orderedGuides &&
				orderedGuides.map(orderedGuide => (
					<GuideRowComponent
						key={orderedGuide.id}
						guide={orderedGuide}
						program={program}
						programProgress={programProgress ?? 0}
					/>
				))}
			{showFooter && (
				<Footer>
					{formatMessage(messages.footer, {
						count: orderedGuides?.length ?? 0,
						duration: formatMessage(messages.footerDuration, {
							hours: Math.floor(totalMinutes / 60),
							minutes: totalMinutes % 60,
						}),
					})}
				</Footer>
			)}
		</Wrapper>
	);
};

export default GuideList;
